/**
 * KsefConfigTabs — Główny kontener trybu Tabsów (dla wracających userów).
 *
 * Pobiera dane konfiguracji KSeF z backendu i renderuje 4 zakładki:
 * Status / Podmiot / Certyfikat / Diagnostyka.
 *
 * Używa Shadcn <Tabs> z /app/frontend/src/components/ui/tabs.jsx.
 *
 * NIE jest jeszcze podpięty pod główny route /ksef-config — w Kroku 2
 * dostępny TYLKO pod tymczasowym route /ksef-config-preview-tabs.
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertCircle, Activity, User, FileCheck, Shield, Lock } from "lucide-react";

import { getAdminConfig, getSavedNip, getStorageInfo } from "./services/ksefApi";

import { TabStatus } from "./tabs/TabStatus";
import { TabPodmiot } from "./tabs/TabPodmiot";
import { TabCertyfikat } from "./tabs/TabCertyfikat";
import { TabHaslo } from "./tabs/TabHaslo";
import { TabDiagnostyka } from "./tabs/TabDiagnostyka";

const ENVIRONMENT = "PROD"; // Aplikacja pracuje wyłącznie na produkcyjnym KSeF.

export const KsefConfigTabs = () => {
  const [activeTab, setActiveTab] = useState("status");
  const [adminConfig, setAdminConfig] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [savedNip, setSavedNip] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, storageRes, nipRes] = await Promise.all([
        getAdminConfig().catch(() => ({ data: null })),
        getStorageInfo().catch(() => ({ data: null })),
        getSavedNip().catch(() => ({ data: null })),
      ]);

      setAdminConfig(cfgRes.data || null);
      setStorageInfo(storageRes.data || null);
      setSavedNip(nipRes.data?.company_nip || cfgRes.data?.company_nip || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Statusy do badge'y po prawej stronie tytułu
  // Od 2026-02: badge "Połączono" tylko gdy `is_operational` == true
  // (rzeczywisty test dekrypcji + walidacja pary cert/klucz/hasło).
  // Sam `has_cert` (obecność pól) nie wystarcza — patrz
  // docs/KSEF_AUTOSYNC_CERT_CONFIG_AUDIT.md.
  const isOperational = !!adminConfig?.is_operational && !!savedNip;
  const hasCertPresent = !!adminConfig?.has_cert && !!savedNip;
  const operationalErrorCode = adminConfig?.operational_error_code || "";
  const operationalMessage = adminConfig?.operational_message || "";

  let headerBadge = null;
  if (!loading) {
    if (isOperational) {
      headerBadge = (
        <Badge
          className="bg-green-100 text-green-800 hover:bg-green-100"
          data-testid="ksef-config-badge-connected"
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Połączono
        </Badge>
      );
    } else if (hasCertPresent) {
      // Certyfikat wgrany, ale para nie działa (np. KSEF_KEY_DECRYPT_FAILED).
      headerBadge = (
        <Badge
          className="bg-orange-100 text-orange-800 hover:bg-orange-100"
          title={`${operationalErrorCode}${operationalMessage ? ": " + operationalMessage : ""}`}
          data-testid="ksef-config-badge-needs-repair"
        >
          <AlertCircle className="w-3 h-3 mr-1" />
          Skonfigurowano — wymaga naprawy
        </Badge>
      );
    } else {
      headerBadge = (
        <Badge
          className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
          data-testid="ksef-config-badge-incomplete"
        >
          <AlertCircle className="w-3 h-3 mr-1" />
          Niekompletne
        </Badge>
      );
    }
  }

  return (
    <div className="space-y-6" data-testid="ksef-config-tabs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F1F3D]">Konfiguracja KSeF</h1>
          <p className="text-sm text-gray-500 mt-1">
            Zarządzaj połączeniem z Krajowym Systemem e-Faktur
          </p>
        </div>
        {headerBadge}
      </div>

      {/* Tabs */}
      <Card className="border border-gray-200">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <CardHeader className="border-b border-gray-100 pb-0">
            <TabsList className="bg-transparent p-0 h-auto justify-start gap-1 w-full overflow-x-auto">
              <TabsTrigger
                value="status"
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none rounded-md px-4 py-2"
                data-testid="tab-trigger-status"
              >
                <Activity className="w-4 h-4 mr-2" />
                Status
              </TabsTrigger>
              <TabsTrigger
                value="podmiot"
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none rounded-md px-4 py-2"
                data-testid="tab-trigger-podmiot"
              >
                <User className="w-4 h-4 mr-2" />
                Podmiot
              </TabsTrigger>
              <TabsTrigger
                value="certyfikat"
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none rounded-md px-4 py-2"
                data-testid="tab-trigger-certyfikat"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                Pliki cert/klucz
              </TabsTrigger>
              <TabsTrigger
                value="haslo"
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none rounded-md px-4 py-2"
                data-testid="tab-trigger-haslo"
              >
                <Lock className="w-4 h-4 mr-2" />
                Hasło klucza
              </TabsTrigger>
              <TabsTrigger
                value="diagnostyka"
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none rounded-md px-4 py-2"
                data-testid="tab-trigger-diagnostyka"
              >
                <Shield className="w-4 h-4 mr-2" />
                Diagnostyka
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <CardContent className="pt-6">
            {loading ? (
              <CardLoading />
            ) : (
              <>
                <TabsContent value="status" className="m-0">
                  <TabStatus
                    adminConfig={adminConfig}
                    storageInfo={storageInfo}
                    savedNip={savedNip}
                    environment={ENVIRONMENT}
                    onRefresh={fetchAll}
                  />
                </TabsContent>

                <TabsContent value="podmiot" className="m-0">
                  <TabPodmiot
                    savedNip={savedNip}
                    environment={ENVIRONMENT}
                    onNipSaved={(newNip) => {
                      setSavedNip(newNip);
                      fetchAll();
                    }}
                  />
                </TabsContent>

                <TabsContent value="certyfikat" className="m-0">
                  <TabCertyfikat
                    adminConfig={adminConfig}
                    savedNip={savedNip}
                    onSaved={fetchAll}
                  />
                </TabsContent>

                <TabsContent value="haslo" className="m-0">
                  <TabHaslo
                    adminConfig={adminConfig}
                    savedNip={savedNip}
                    onSaved={fetchAll}
                  />
                </TabsContent>

                <TabsContent value="diagnostyka" className="m-0">
                  <TabDiagnostyka onTested={fetchAll} />
                </TabsContent>
              </>
            )}
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
};

const CardLoading = () => (
  <div className="flex items-center justify-center py-12" data-testid="tabs-loading">
    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

export default KsefConfigTabs;
